import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Edit3,
  Trash2,
  CheckCircle,
  RotateCcw,
  ArrowRightLeft,
  TrendingUp,
  TrendingDown,
  Wallet,
  Calendar,
  User,
  Hash,
  MessageSquare,
  AlertCircle,
  CreditCard,
  Eye,
  Code,
  Activity,
  ArrowDownLeft,
  ArrowUpRight,
  Lock,
} from 'lucide-react';
import { useEntity } from '../contexts/EntityContext';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import Modal, { ConfirmModal } from '../components/ui/Modal';
import { LoadingState } from '../components/ui/Spinner';
import { useToast } from '../components/ui/Toast';
import { formatAmount, formatDate, calculateDisplayRate, formatDisplayRate } from '../lib/format';
import { DebtPaymentModal } from '../components/cashier/DebtPaymentModal';
import { TxHashField } from '../components/cashier/TxHashField';
import { detectNetwork, networkLabel, explorerUrl } from '../lib/network';
import { ExternalLink } from 'lucide-react';

export default function TransactionDetailPage() {
  const { t } = useTranslation(['transactions', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentEntity } = useEntity();
  const { user } = useAuth();
  const { success, error: showError } = useToast();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showJson, setShowJson] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['transaction', currentEntity?.id, id],
    queryFn: () => api.transactions.get(currentEntity!.id, id!),
    enabled: !!currentEntity && !!id,
  });

  const { data: walletsData } = useQuery({
    queryKey: ['wallets', currentEntity?.id],
    queryFn: () => api.admin.wallets.list(currentEntity!.id),
    enabled: !!currentEntity,
  });


  const deleteMutation = useMutation({
    mutationFn: () => api.transactions.delete(currentEntity!.id, id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      success('Transaction deleted');
      navigate('/transactions');
    },
    onError: (err: any) => {
      showError(err.message || 'Failed to delete transaction');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) =>
      api.transactions.update(currentEntity!.id, id!, {
        ...data,
        version: tx!.version,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transaction', currentEntity?.id, id] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      success('Transaction updated');
      setShowEditModal(false);
    },
    onError: (err: any) => {
      showError(err.message || 'Failed to update transaction');
    },
  });

  if (!currentEntity || !id) return null;
  if (isLoading) return <LoadingState message="Loading transaction..." />;
  if (error) return <div className="card text-error">Error loading transaction</div>;
  if (!data) return null;

  const tx = data.data;
  const isAdmin = user?.role === 'admin';
  const canEdit = isAdmin || user?.role === 'accountant' ||
    (user?.role === 'cashier' && tx.employeeId === user.id);
  const canDelete = isAdmin || (user?.role === 'cashier' && tx.employeeId === user.id);

  const hasDebtIn = tx.debtAmountIn && parseFloat(String(tx.debtAmountIn)) > 0;
  const hasDebtOut = tx.debtAmountOut && parseFloat(String(tx.debtAmountOut)) > 0;

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'exchange': return <ArrowRightLeft size={16} />;
      case 'expense': return <TrendingDown size={16} />;
      case 'deposit': return <TrendingUp size={16} />;
      case 'withdrawal': return <CreditCard size={16} />;
      default: return <ArrowRightLeft size={16} />;
    }
  };

  return (
    <div className="tx-detail">
      {/* Page Header */}
      <div className="page-header">
        <div className="header-left">
          <Link to="/transactions" className="back-btn">
            <ArrowLeft size={18} />
            <span>{t('common:actions.back')}</span>
          </Link>
          <div className="header-title-group">
            <h1 className="page-title">
              {t('transactionDetail')}
              <span className="tx-id">#{tx.id.slice(0, 8)}</span>
            </h1>
            <div className="header-badges">
              <span className={`type-badge type-${tx.type}`}>
                {getTypeIcon(tx.type)}
                {t(`types.${tx.type}`)}
              </span>
              <span className={`status-badge status-${tx.status}`}>
                <span className="status-dot"></span>
                {t(`status.${tx.status}`)}
              </span>
            </div>
          </div>
        </div>
        <div className="header-actions">
          {canEdit && (
            <button className="action-btn secondary" onClick={() => setShowEditModal(true)}>
              <Edit3 size={16} />
              Редактировать
            </button>
          )}
          {tx.status === 'open' && canEdit && (
            <button
              className="action-btn primary"
              onClick={() => {
                if (hasDebtIn || hasDebtOut) {
                  setShowPaymentModal(true);
                } else {
                  setShowCloseConfirm(true);
                }
              }}
              disabled={updateMutation.isPending}
            >
              <CheckCircle size={16} />
              {hasDebtIn || hasDebtOut ? 'Record Payment' : 'Close Transaction'}
            </button>
          )}
          {tx.status === 'closed' && canEdit && (
            <button
              className="action-btn secondary"
              onClick={() => setShowReopenConfirm(true)}
              disabled={updateMutation.isPending}
            >
              <RotateCcw size={16} />
              Reopen
            </button>
          )}
          {canDelete && (
            <button
              className="action-btn danger"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 size={16} />
              Удалить
            </button>
          )}
          <Link
            to={`/movements?transactionId=${tx.id}`}
            className="action-btn secondary"
            style={{ textDecoration: 'none' }}
          >
            <Activity size={16} />
            Movements
          </Link>
          <button
            className={`action-btn ${showJson ? 'primary' : 'secondary'}`}
            onClick={() => setShowJson(!showJson)}
          >
            {showJson ? <Eye size={16} /> : <Code size={16} />}
            {showJson ? 'Hide Raw Data' : 'View Raw Data'}
          </button>
        </div>
      </div>

      {showJson && (
        <div className="raw-data-section">
          <div className="detail-card">
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Code size={18} />
                <span className="card-label">RAW JSON</span>
              </div>
            </div>
            <pre className="json-content">
              {JSON.stringify(tx, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {/* Closed period indicator */}
      {tx.status === 'closed' && tx.inClosedPeriod && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '10px 14px',
            borderRadius: '8px',
            background: 'var(--ctp-yellow, #f9e2af)18',
            border: '1px solid var(--ctp-yellow, #f9e2af)44',
            color: 'var(--ctp-yellow, #f9e2af)',
            fontSize: '13px',
            fontWeight: 500,
            marginBottom: '12px',
          }}
        >
          <Lock size={16} style={{ flexShrink: 0 }} />
          {t('closedPeriod.banner')}
        </div>
      )}

      {/* Warnings */}
      <TransactionWarnings tx={tx} entityId={currentEntity.id} />

      {/* Main Content Grid */}
      <div className="content-grid">
        {/* Left Column - Transaction Details */}
        <div className="details-column">
          {/* Details Card */}
          <div className="detail-card">
            <div className="card-header">
              <span className="card-label">TRANSACTION DETAILS</span>
            </div>
            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-icon">
                  <Hash size={16} />
                </div>
                <div className="detail-content">
                  <span className="detail-label">Transaction ID</span>
                  <span className="detail-value mono">{tx.id}</span>
                </div>
              </div>
              <div className="detail-item">
                <div className="detail-icon">
                  <Calendar size={16} />
                </div>
                <div className="detail-content">
                  <span className="detail-label">Дата IN (создана)</span>
                  <span className="detail-value" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span>
                      <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', width: '40px', display: 'inline-block' }}>Лок.:</span>
                      {new Date(tx.createdAt).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </span>
                    <span>
                      <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', width: '40px', display: 'inline-block' }}>Mad.:</span>
                      {new Date(tx.createdAt).toLocaleString('ru-RU', { timeZone: 'Europe/Madrid', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </span>
                  </span>
                </div>
              </div>
              {tx.transactionDate && (
                <div className="detail-item">
                  <div className="detail-icon" style={{ color: 'var(--ctp-teal)' }}>
                    <CheckCircle size={16} />
                  </div>
                  <div className="detail-content">
                    <span className="detail-label">Дата OUT (д. сделки)</span>
                    <span className="detail-value" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span>
                        <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', width: '40px', display: 'inline-block' }}>Лок.:</span>
                        {tx.transactionDate.slice(0, 10).split('-').reverse().join('.')}
                      </span>
                      <span>
                        <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', width: '40px', display: 'inline-block' }}>Mad.:</span>
                        {tx.transactionDate.slice(0, 10).split('-').reverse().join('.')}
                      </span>
                    </span>
                  </div>
                </div>
              )}
              <div className="detail-item">
                <div className="detail-icon">
                  <User size={16} />
                </div>
                <div className="detail-content">
                  <span className="detail-label">Customer</span>
                  <span className="detail-value">{tx.clientName || '-'}</span>
                </div>
              </div>
              <div className="detail-item">
                <div className="detail-icon">
                  <User size={16} />
                </div>
                <div className="detail-content">
                  <span className="detail-label">Employee</span>
                  <span className="detail-value">{tx.employeeName}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Description Card */}
          {tx.comment && (
            <div className="detail-card">
              <div className="card-header">
                <div className="card-header-icon">
                  <MessageSquare size={16} />
                </div>
                <span className="card-label">DESCRIPTION</span>
              </div>
              <p className="description-text">{tx.comment}</p>
            </div>
          )}


        </div>{/* /details-column */}

        {/* Right Column - Amounts */}
        <div className="amounts-column">
          {/* Amounts Section */}
          {tx.type === 'exchange' ? (
            <>
              {/* Amount In Card */}
              <div className="amount-card amount-in">
                <div className="amount-header">
                  <span className="amount-label">AMOUNT IN</span>
                  <div className="amount-icon income">
                    <TrendingUp size={20} />
                  </div>
                </div>
                <div className="amount-value">
                  <span className="currency-symbol">
                    {tx.currencyIn === 'EUR' ? '€' : tx.currencyIn === 'USD' ? '$' : ''}
                  </span>
                  {formatAmount(tx.amountIn, tx.currencyIn)}
                  <span className="currency-code">{tx.currencyIn}</span>
                </div>
                <div className="amount-footer">
                  <Wallet size={14} />
                  <span>{tx.walletInName || 'No wallet'}</span>
                </div>
              </div>

              {/* Exchange Rate Card */}
              <div className="rate-card">
                <div className="rate-header">
                  <span className="rate-label">EXCHANGE RATE</span>
                  <ArrowRightLeft size={18} className="rate-icon" />
                </div>
                <div className="rate-value">
                  {formatDisplayRate(calculateDisplayRate(tx))}
                </div>
                {tx.oracleRate && (
                  <div className="rate-oracle">
                    <span className="oracle-label">Oracle Rate:</span>
                    <span className="oracle-value">{parseFloat(tx.oracleRate).toFixed(4)}</span>
                  </div>
                )}
              </div>

              {/* Amount Out Card */}
              <div className="amount-card amount-out">
                <div className="amount-header">
                  <span className="amount-label">AMOUNT OUT</span>
                  <div className="amount-icon expense">
                    <TrendingDown size={20} />
                  </div>
                </div>
                <div className="amount-value">
                  <span className="currency-symbol">
                    {tx.currencyOut === 'EUR' ? '€' : tx.currencyOut === 'USD' ? '$' : ''}
                  </span>
                  {formatAmount(tx.amountOut, tx.currencyOut)}
                  <span className="currency-code">{tx.currencyOut}</span>
                </div>
                <div className="amount-footer">
                  <Wallet size={14} />
                  <span>{tx.walletOutName || 'No wallet'}</span>
                </div>
              </div>
            </>
          ) : (
            <div className={`amount-card single ${tx.type === 'expense' || tx.type === 'withdrawal' ? 'amount-out' : 'amount-in'}`}>
              <div className="amount-header">
                <span className="amount-label">AMOUNT</span>
                <div className={`amount-icon ${tx.type === 'expense' || tx.type === 'withdrawal' ? 'expense' : 'income'}`}>
                  {tx.type === 'expense' || tx.type === 'withdrawal' ? <TrendingDown size={20} /> : <TrendingUp size={20} />}
                </div>
              </div>
              <div className="amount-value">
                <span className="currency-symbol">
                  {tx.currency === 'EUR' ? '€' : tx.currency === 'USD' ? '$' : ''}
                </span>
                {formatAmount(tx.amount, tx.currency)}
                <span className="currency-code">{tx.currency}</span>
              </div>
              <div className="amount-footer">
                <Wallet size={14} />
                <span>{tx.walletName || 'No wallet'}</span>
                {tx.type === 'expense' && tx.category && (
                  <>
                    <span className="footer-divider">•</span>
                    <span>{tx.category}</span>
                  </>
                )}
              </div>
            </div>
          )}

          {hasDebtIn && (
            <div className="financial-card debt-card">
              <div className="financial-header">
                <span className="financial-label">DEBT IN (EXPECTED)</span>
                <AlertCircle size={16} className="debt-icon" />
              </div>
              <div className="financial-value negative" style={{ color: 'var(--ctp-peach)' }}>
                {formatAmount(tx.debtAmountIn, tx.currencyIn)} {tx.currencyIn}
              </div>
              {tx.expectedPaymentDateIn && typeof tx.expectedPaymentDateIn === 'string' && (
                <div className="debt-expected">
                  Due: {formatDate(tx.expectedPaymentDateIn)}
                </div>
              )}
              <Link to="/debt" className="debt-link">
                View Debts →
              </Link>
            </div>
          )}

          {hasDebtOut && (
            <div className="financial-card debt-card">
              <div className="financial-header">
                <span className="financial-label">DEBT OUT (PROMISED)</span>
                <AlertCircle size={16} className="debt-icon" />
              </div>
              <div className="financial-value negative" style={{ color: 'var(--ctp-peach)' }}>
                {formatAmount(tx.debtAmountOut, tx.currencyOut)} {tx.currencyOut}
              </div>
              {tx.expectedPaymentDateOut && typeof tx.expectedPaymentDateOut === 'string' && (
                <div className="debt-expected">
                  Due: {formatDate(tx.expectedPaymentDateOut)}
                </div>
              )}
              <Link to="/debt" className="debt-link">
                View Debts →
              </Link>
            </div>
          )}

          {(() => {
            const wallets = walletsData?.data ?? [];
            const wIn = wallets.find(w => w.id === tx.walletInId);
            const wOut = wallets.find(w => w.id === tx.walletOutId);
            const wSingle = wallets.find(w => w.id === tx.walletId);

            const rows: { label: string; hash: string; address?: string | null; type?: string }[] = [];
            if (tx.type === 'exchange') {
              if (tx.txHashIn && wIn?.type === 'crypto') {
                rows.push({ label: `TX Hash IN (${tx.currencyIn})`, hash: tx.txHashIn, address: wIn.address, type: wIn.type });
              }
              if (tx.txHashOut && wOut?.type === 'crypto') {
                rows.push({ label: `TX Hash OUT (${tx.currencyOut})`, hash: tx.txHashOut, address: wOut.address, type: wOut.type });
              }
            } else if (tx.txHash && wSingle?.type === 'crypto') {
              rows.push({ label: `TX Hash (${tx.currency})`, hash: tx.txHash, address: wSingle.address, type: wSingle.type });
            }
            if (rows.length === 0) return null;

            return (
              <div className="financial-card" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div className="financial-header">
                  <span className="financial-label">BLOCKCHAIN TX</span>
                  <Hash size={16} />
                </div>
                {rows.map((r, i) => {
                  const net = detectNetwork(r.address);
                  const url = explorerUrl(net, r.hash);
                  return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {r.label} {net !== 'unknown' && <span style={{ marginLeft: 6, color: 'var(--ctp-teal)' }}>{networkLabel(net)}</span>}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <code style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--color-text)', wordBreak: 'break-all' }}>{r.hash}</code>
                        {url && (
                          <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--ctp-teal)', display: 'inline-flex', flexShrink: 0 }} title="Open in explorer">
                            <ExternalLink size={13} />
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </div>

      {/* ─── Inline Wallet Movements ─── */}
      <TransactionMovementsInline transactionId={tx.id} entityId={currentEntity.id} />

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => deleteMutation.mutate()}
        title="Удалить транзакцию?"
        message="Транзакция будет удалена безвозвратно. Все связанные движения средств (wallet movements) также будут удалены автоматически."
        confirmText="Удалить"
        confirmVariant="danger"
        isLoading={deleteMutation.isPending}
      />

      <ConfirmModal
        isOpen={showCloseConfirm}
        onClose={() => setShowCloseConfirm(false)}
        onConfirm={() => {
          updateMutation.mutate({
            status: 'closed',
            transactionDate: new Date().toISOString().split('T')[0],
          });
          setShowCloseConfirm(false);
        }}
        title="Close Transaction"
        message="Are you sure you want to close this transaction? This will mark it as completed."
        confirmText="Close Transaction"
        confirmVariant="primary"
        isLoading={updateMutation.isPending}
      />

      <ConfirmModal
        isOpen={showReopenConfirm}
        onClose={() => setShowReopenConfirm(false)}
        onConfirm={() => {
          updateMutation.mutate({ status: 'open' });
          setShowReopenConfirm(false);
        }}
        title="Reopen Transaction"
        message="Are you sure you want to reopen this transaction? This will mark it as pending again."
        confirmText="Reopen"
        confirmVariant="primary"
        isLoading={updateMutation.isPending}
      />

      {showEditModal && (
        <EditModal
          transaction={tx}
          entityId={currentEntity.id}
          onClose={() => setShowEditModal(false)}
          onSave={(data) => updateMutation.mutate(data)}
          isLoading={updateMutation.isPending}
        />
      )}

      {showPaymentModal && (
        <DebtPaymentModal
          transactionId={tx.id}
          entityId={currentEntity.id}
          walletOutId={tx.walletOutId}
          walletInId={tx.walletInId}
          debtAmountOut={tx.debtAmountOut}
          debtAmountIn={tx.debtAmountIn}
          originalDebtAmountOut={tx.originalDebtAmountOut}
          originalDebtAmountIn={tx.originalDebtAmountIn}
          currencyOut={tx.currencyOut}
          currencyIn={tx.currencyIn}
          onClose={() => setShowPaymentModal(false)}
        />
      )}

      <style>{`
        .tx-detail {
          display: flex;
          flex-direction: column;
          gap: 24px;
          max-width: 1200px;
          margin: 0 auto;
          padding-top: 24px;
        }

        /* Page Header */
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 16px;
        }

        .header-left {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .back-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: 6px;
          color: var(--color-text-muted);
          font-size: 13px;
          font-weight: 500;
          text-decoration: none;
          transition: all 0.15s;
          width: fit-content;
        }

        .back-btn:hover {
          border-color: var(--ctp-teal);
          color: var(--ctp-teal);
        }

        .header-title-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .page-title {
          font-size: 24px;
          font-weight: 700;
          color: var(--color-text);
          display: flex;
          align-items: center;
          gap: 10px;
          margin: 0;
        }

        .tx-id {
          font-family: 'SF Mono', 'Consolas', monospace;
          font-size: 16px;
          font-weight: 500;
          color: var(--color-text-muted);
        }

        .header-badges {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        /* Type Badges */
        .type-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .type-exchange {
          background: rgba(245, 158, 11, 0.15);
          color: var(--ctp-yellow, #f59e0b);
        }

        .type-expense {
          background: rgba(239, 68, 68, 0.15);
          color: var(--ctp-red, #ef4444);
        }

        .type-deposit {
          background: rgba(16, 185, 129, 0.15);
          color: var(--ctp-green, #10b981);
        }

        .type-withdrawal {
          background: rgba(59, 130, 246, 0.15);
          color: var(--ctp-blue, #3b82f6);
        }

        .type-transfer {
          background: rgba(107, 114, 128, 0.15);
          color: var(--color-text-muted);
        }

        /* Status Badges */
        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .status-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
        }

        .status-closed {
          background: rgba(16, 185, 129, 0.1);
          color: var(--ctp-green, #10b981);
        }

        .status-closed .status-dot {
          background: var(--ctp-green, #10b981);
        }

        .status-open {
          background: rgba(245, 158, 11, 0.1);
          color: var(--ctp-yellow, #f59e0b);
        }

        .status-open .status-dot {
          background: var(--ctp-yellow, #f59e0b);
        }

        /* Header Actions */
        .header-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .action-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          border: none;
          transition: all 0.15s;
        }

        .action-btn.primary {
          background: var(--ctp-teal);
          color: #ffffff;
        }

        .action-btn.primary:hover:not(:disabled) {
          background: #0f766e;
        }

        .action-btn.secondary {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          color: var(--color-text);
        }

        .action-btn.secondary:hover:not(:disabled) {
          border-color: var(--ctp-teal);
          color: var(--ctp-teal);
        }

        .action-btn.danger {
          background: rgba(239, 68, 68, 0.1);
          color: var(--ctp-red, #ef4444);
        }

        .action-btn.danger:hover:not(:disabled) {
          background: var(--ctp-red, #ef4444);
          color: #ffffff;
        }

        .action-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Content Grid */
        .content-grid {
          display: grid;
          grid-template-columns: 1fr 400px;
          gap: 24px;
        }

        .details-column {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .amounts-column {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        /* Detail Card */
        .detail-card {
          background: var(--color-surface);
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid var(--color-border);
        }

        .card-header-icon {
          color: var(--color-text-muted);
        }

        .card-label {
          font-size: 11px;
          font-weight: 600;
          color: var(--color-text-muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .detail-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .detail-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }

        .detail-icon {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          background: var(--color-surface-hover);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--color-text-muted);
          flex-shrink: 0;
        }

        .detail-content {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .detail-label {
          font-size: 11px;
          font-weight: 600;
          color: var(--color-text-muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .detail-value {
          font-size: 14px;
          font-weight: 500;
          color: var(--color-text);
        }

        .detail-value.mono {
          font-family: 'SF Mono', 'Consolas', monospace;
          font-size: 13px;
        }

        /* Description */
        .description-text {
          white-space: pre-wrap;
          color: var(--color-text);
          line-height: 1.6;
          font-size: 14px;
        }

        /* Meta Card */
        .meta-card {
          background: var(--color-surface-hover);
          padding: 16px 20px;
        }

        .meta-items {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: var(--color-text-muted);
        }

        .meta-label {
          color: var(--color-text-muted);
        }

        .meta-value {
          color: var(--color-text);
          font-weight: 500;
        }

        .meta-value.mono {
          font-family: 'SF Mono', 'Consolas', monospace;
        }

        /* Amount Cards */
        .amount-card {
          background: var(--color-surface);
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          position: relative;
          overflow: hidden;
        }

        .amount-card.amount-in {
          border-left: 4px solid var(--ctp-green, #10b981);
        }

        .amount-card.amount-out {
          border-left: 4px solid var(--ctp-red, #ef4444);
        }

        .amount-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
        }

        .amount-label {
          font-size: 11px;
          font-weight: 600;
          color: var(--color-text-muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .amount-icon {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .amount-icon.income {
          background: rgba(16, 185, 129, 0.1);
          color: var(--ctp-green, #10b981);
        }

        .amount-icon.expense {
          background: rgba(239, 68, 68, 0.1);
          color: var(--ctp-red, #ef4444);
        }

        .amount-value {
          font-size: 28px;
          font-weight: 700;
          color: var(--color-text);
          font-family: 'Inter', -apple-system, sans-serif;
          margin-bottom: 12px;
          display: flex;
          align-items: baseline;
          gap: 6px;
        }

        .currency-symbol {
          font-size: 20px;
          color: var(--color-text-muted);
        }

        .currency-code {
          font-size: 14px;
          font-weight: 600;
          color: var(--ctp-teal);
          margin-left: 4px;
        }

        .amount-footer {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: var(--color-text-muted);
        }

        .footer-divider {
          color: var(--color-border);
        }

        /* Rate Card */
        .rate-card {
          background: var(--color-surface-hover);
          border-radius: 8px;
          padding: 20px;
          text-align: center;
        }

        .rate-header {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }

        .rate-label {
          font-size: 11px;
          font-weight: 600;
          color: var(--color-text-muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .rate-icon {
          color: var(--color-text-muted);
        }

        .rate-value {
          font-size: 28px;
          font-weight: 700;
          color: var(--color-text);
          font-family: 'SF Mono', 'Consolas', monospace;
          margin-bottom: 8px;
        }

        .rate-oracle {
          display: flex;
          justify-content: center;
          gap: 6px;
          font-size: 12px;
        }

        .oracle-label {
          color: var(--color-text-muted);
        }

        .oracle-value {
          font-family: 'SF Mono', 'Consolas', monospace;
          font-weight: 500;
          color: var(--color-text);
        }

        /* Financial Cards */
        .financial-card {
          background: var(--color-surface);
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .profit-card {
          border-left: 4px solid var(--ctp-teal);
        }

        .debt-card {
          border-left: 4px solid var(--ctp-red, #ef4444);
          background: rgba(239, 68, 68, 0.05);
        }

        .financial-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .financial-label {
          font-size: 11px;
          font-weight: 600;
          color: var(--color-text-muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .profit-icon {
          color: var(--ctp-teal);
        }

        .debt-icon {
          color: var(--ctp-red, #ef4444);
        }

        .financial-value {
          font-size: 24px;
          font-weight: 700;
          font-family: 'SF Mono', 'Consolas', monospace;
        }

        .financial-value.positive {
          color: var(--ctp-green, #10b981);
        }

        .financial-value.negative {
          color: var(--ctp-red, #ef4444);
        }

        .debt-expected {
          font-size: 13px;
          color: var(--color-text-muted);
          margin-top: 8px;
        }

        .debt-link {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          margin-top: 12px;
          padding: 8px 14px;
          background: var(--ctp-teal);
          color: #ffffff;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          text-decoration: none;
          transition: background 0.15s;
        }

        .debt-link:hover {
          background: #0f766e;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .content-grid {
            grid-template-columns: 1fr;
          }

          .amounts-column {
            order: -1;
          }
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            align-items: stretch;
          }

          .header-actions {
            justify-content: flex-start;
          }

          .detail-grid {
            grid-template-columns: 1fr;
          }

          .amount-value {
            font-size: 24px;
          }

          .currency-symbol {
            font-size: 18px;
            color: var(--color-text-muted);
          }
        }

        .raw-data-section {
          animation: slideDown 0.3s ease-out;
        }

        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .raw-table-container {
          overflow-x: auto;
          margin-top: 12px;
        }

        .raw-table {
          width: 100%;
          border-collapse: collapse;
          font-family: 'SF Mono', 'Consolas', monospace;
          font-size: 13px;
        }

        .raw-table th, .raw-table td {
          padding: 12px;
          text-align: left;
          border-bottom: 1px solid var(--color-border);
        }

        .raw-table th {
          background: var(--color-surface-hover);
          color: var(--color-text-muted);
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .raw-table .field-name {
          color: var(--ctp-teal);
          font-weight: 600;
          width: 30%;
        }

        .raw-table .field-value {
          color: var(--color-text);
          word-break: break-all;
          white-space: pre-wrap;
        }

        .json-content {
          background: #1e1e2e;
          color: #cdd6f4;
          padding: 16px;
          border-radius: 8px;
          font-family: 'SF Mono', 'Consolas', monospace;
          font-size: 12px;
          overflow-x: auto;
          margin: 0;
        }
      `}</style>
    </div>
  );
}

// ─── TransactionWarnings ─────────────────────────────────────────────────────

function TransactionWarnings({
  tx,
  entityId,
}: {
  tx: any;
  entityId: string;
}) {
  const { data } = useQuery({
    queryKey: ['movements', entityId, 'tx', tx.id],
    queryFn: () => api.movements.list(entityId, { transactionId: tx.id }),
  });

  const movements = (data?.data ?? []).filter((m: any) => !m.isDebtPayment);
  const warnings: string[] = [];

  // 1. Check: Дата OUT < Дата IN
  if (tx.transactionDate && tx.createdAt) {
    const dateIn = new Date(tx.createdAt);
    // Compare date parts only
    const outDay = tx.transactionDate.slice(0, 10);
    const inDay = dateIn.toISOString().slice(0, 10);
    if (outDay < inDay) {
      warnings.push(`Дата OUT (${outDay.split('-').reverse().join('.')}) раньше чем Дата IN (${inDay.split('-').reverse().join('.')})`);
    }
  }

  // 2. Check: amounts vs wallet_movements
  if (movements.length > 0) {
    if (tx.type === 'exchange') {
      const amountIn = parseFloat(String(tx.amountIn || 0));
      const amountOut = parseFloat(String(tx.amountOut || 0));

      // Positive movements = money in, negative = money out
      const movementIn = movements
        .filter((m: any) => parseFloat(m.amount) > 0)
        .reduce((sum: number, m: any) => sum + Math.abs(parseFloat(m.amount)), 0);
      const movementOut = movements
        .filter((m: any) => parseFloat(m.amount) < 0)
        .reduce((sum: number, m: any) => sum + Math.abs(parseFloat(m.amount)), 0);

      if (amountIn > 0 && Math.abs(amountIn - movementIn) > 0.01) {
        warnings.push(`Amount IN (${amountIn.toFixed(2)}) не совпадает с суммой входящих движений (${movementIn.toFixed(2)})`);
      }
      if (amountOut > 0 && Math.abs(amountOut - movementOut) > 0.01) {
        warnings.push(`Amount OUT (${amountOut.toFixed(2)}) не совпадает с суммой исходящих движений (${movementOut.toFixed(2)})`);
      }
    } else {
      const amount = parseFloat(String(tx.amount || 0));
      const movementTotal = movements.reduce((sum: number, m: any) => sum + Math.abs(parseFloat(m.amount)), 0);

      if (amount > 0 && Math.abs(amount - movementTotal) > 0.01) {
        warnings.push(`Amount (${amount.toFixed(2)}) не совпадает с суммой движений (${movementTotal.toFixed(2)})`);
      }
    }
  }

  // 3. Check: movement dates outside IN/OUT range
  if (movements.length > 0) {
    const dateIn = new Date(tx.createdAt);
    const dateInDay = dateIn.toISOString().slice(0, 10);
    const dateOutDay = tx.transactionDate ? tx.transactionDate.slice(0, 10) : null;

    // Determine the valid range: min(dateIn, dateOut) to max(dateIn, dateOut)
    let rangeStart = dateInDay;
    let rangeEnd = dateOutDay || dateInDay;
    if (dateOutDay && dateOutDay < dateInDay) {
      rangeStart = dateOutDay;
      rangeEnd = dateInDay;
    }

    for (const m of movements) {
      const mDate = new Date(m.createdAt).toISOString().slice(0, 10);
      if (mDate < rangeStart || mDate > rangeEnd) {
        const mDateFormatted = mDate.split('-').reverse().join('.');
        const rangeStartFormatted = rangeStart.split('-').reverse().join('.');
        const rangeEndFormatted = rangeEnd.split('-').reverse().join('.');
        warnings.push(
          `Движение от ${mDateFormatted} (${m.walletName}) вне интервала ${rangeStartFormatted} — ${rangeEndFormatted}`
        );
      }
    }
  }

  if (warnings.length === 0) return null;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    }}>
      {warnings.map((w, i) => (
        <div key={i} style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '10px 14px',
          borderRadius: '8px',
          background: 'var(--ctp-peach, #fab387)18',
          border: '1px solid var(--ctp-peach, #fab387)44',
          color: 'var(--ctp-peach, #fab387)',
          fontSize: '13px',
          fontWeight: 500,
        }}>
          <AlertCircle size={16} style={{ flexShrink: 0 }} />
          {w}
        </div>
      ))}
    </div>
  );
}

// ─── TransactionMovementsInline ──────────────────────────────────────────────

function TransactionMovementsInline({
  transactionId,
  entityId,
}: {
  transactionId: string;
  entityId: string;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['movements', entityId, 'tx', transactionId],
    queryFn: () => api.movements.list(entityId, { transactionId }),
  });

  const movements = data?.data ?? [];

  const formatAmt = (amount: string) => {
    const n = parseFloat(amount);
    const abs = Math.abs(n).toFixed(Math.abs(n) >= 1 ? 2 : 6);
    return n >= 0 ? `+${abs}` : `-${abs}`;
  };

  const thStyle: React.CSSProperties = {
    padding: '10px 12px', textAlign: 'left', fontWeight: 600,
    color: 'var(--color-text-muted)', textTransform: 'uppercase',
    fontSize: '11px', letterSpacing: '0.05em',
  };

  return (
    <>
      <div style={{
        background: 'var(--color-surface)',
        borderRadius: '12px',
        border: '1px solid var(--color-border)',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <Activity size={16} style={{ color: 'var(--color-text-muted)' }} />
          <span style={{ fontWeight: 600, fontSize: '14px' }}>Wallet Movements</span>
          <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>({movements.length})</span>
        </div>

        {isLoading ? (
          <div style={{ padding: '32px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            Загрузка...
          </div>
        ) : movements.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            Нет движений средств для этой транзакции.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-border)', background: 'var(--color-surface-hover)' }}>
                  <th style={thStyle}>Дата</th>
                  <th style={thStyle}>Кошелёк</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Сумма</th>
                  <th style={thStyle}>Тип</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((m) => {
                  const isPositive = parseFloat(m.amount) >= 0;
                  return (
                    <tr key={m.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                        <span style={{ color: 'var(--color-text-muted)', fontFamily: 'monospace' }}>
                          {(() => {
                            const d = new Date(m.createdAt);
                            const date = d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
                            const time = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                            return `${date} ${time}`;
                          })()}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Wallet size={13} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
                          <span style={{ color: 'var(--color-text)' }}>{m.walletName}</span>
                          <span style={{ color: 'var(--color-text-muted)', fontSize: '11px' }}>({m.walletType})</span>
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: 600, color: isPositive ? 'var(--ctp-green)' : 'var(--ctp-red)' }}>
                          {formatAmt(m.amount)} {m.currency}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        {m.isDebtPayment ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '99px', fontSize: '11px', fontWeight: 600, background: 'var(--ctp-peach)22', color: 'var(--ctp-peach)', border: '1px solid var(--ctp-peach)44' }}>
                            {isPositive ? <ArrowDownLeft size={11} /> : <ArrowUpRight size={11} />}
                            Debt Payment
                          </span>
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '99px', fontSize: '11px', fontWeight: 600, background: 'var(--ctp-blue)22', color: 'var(--ctp-blue)', border: '1px solid var(--ctp-blue)44' }}>
                            {isPositive ? <ArrowDownLeft size={11} /> : <ArrowUpRight size={11} />}
                            Regular
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </>
  );
}

// ─── TransactionMovementsModal ──────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function TransactionMovementsModal({
  transactionId,
  entityId,
  onClose,
}: {
  transactionId: string;
  entityId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ['movements', entityId, 'tx', transactionId],
    queryFn: () => api.movements.list(entityId, { transactionId }),
  });

  const movements = data?.data ?? [];

  // Inline edit state for amount
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  // Inline edit state for date
  const [editingDateId, setEditingDateId] = useState<string | null>(null);
  const [editDateValue, setEditDateValue] = useState('');

  // Confirm recalculate dialog state
  const [pendingUpdate, setPendingUpdate] = useState<{ movementId: string; amount: number } | null>(null);

  const updateMutation = useMutation({
    mutationFn: ({ movementId, amount, recalculateTransaction }: { movementId: string; amount: number; recalculateTransaction: boolean }) =>
      api.movements.update(entityId, movementId, { amount, recalculateTransaction }),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['movements', entityId, 'tx', transactionId] });
      queryClient.invalidateQueries({ queryKey: ['transaction', entityId, transactionId] });
      success(vars.recalculateTransaction ? 'Движение и транзакция обновлены' : 'Движение обновлено');
      setEditingId(null);
      setPendingUpdate(null);
    },
    onError: (err: any) => {
      showError(err.message || 'Ошибка при обновлении');
      setPendingUpdate(null);
    },
  });

  const updateDateMutation = useMutation({
    mutationFn: ({ movementId, createdAt }: { movementId: string; createdAt: string }) =>
      api.movements.update(entityId, movementId, { createdAt }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['movements', entityId, 'tx', transactionId] });
      queryClient.invalidateQueries({ queryKey: ['transaction', entityId, transactionId] });
      success('Дата движения обновлена');
      setEditingDateId(null);
    },
    onError: (err: any) => {
      showError(err.message || 'Ошибка при обновлении даты');
    },
  });

  const formatAmt = (amount: string) => {
    const n = parseFloat(amount);
    const abs = Math.abs(n).toFixed(Math.abs(n) >= 1 ? 2 : 6);
    return n >= 0 ? `+${abs}` : `-${abs}`;
  };

  const startEdit = (m: { id: string; amount: string }) => {
    setEditingId(m.id);
    setEditValue(Math.abs(parseFloat(m.amount)).toString());
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const commitEdit = (movementId: string) => {
    const parsed = parseFloat(editValue);
    if (isNaN(parsed) || parsed <= 0) {
      showError('Введите корректную положительную сумму');
      return;
    }
    setPendingUpdate({ movementId, amount: parsed });
  };

  // Date editing helpers
  const toDatetimeLocalValue = (isoStr: string) => {
    const d = new Date(isoStr);
    // Browser is in Madrid timezone, so local formatting is correct
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const startDateEdit = (m: { id: string; createdAt: string }) => {
    setEditingDateId(m.id);
    setEditDateValue(toDatetimeLocalValue(m.createdAt));
  };

  const cancelDateEdit = () => {
    setEditingDateId(null);
    setEditDateValue('');
  };

  const commitDateEdit = (movementId: string) => {
    if (!editDateValue) return;
    // Browser is in Madrid timezone — new Date() interprets datetime-local as local (Madrid) time
    const utcDate = new Date(`${editDateValue}:00`);
    updateDateMutation.mutate({ movementId, createdAt: utcDate.toISOString() });
  };

  const thStyle: React.CSSProperties = {
    padding: '10px 12px', textAlign: 'left', fontWeight: 600,
    color: 'var(--color-text-muted)', textTransform: 'uppercase',
    fontSize: '11px', letterSpacing: '0.05em',
  };

  return (
    <>
      <Modal isOpen onClose={onClose} title="Wallet Movements" size="lg">
        {isLoading ? (
          <div style={{ padding: '32px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            Loading movements…
          </div>
        ) : movements.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            No wallet movements found for this transaction.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-border)', background: 'var(--color-surface-hover)' }}>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Wallet</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                  <th style={thStyle}>Type</th>
                  <th style={{ ...thStyle, textAlign: 'center', width: '48px' }}></th>
                </tr>
              </thead>
              <tbody>
                {movements.map((m) => {
                  const isPositive = parseFloat(m.amount) >= 0;
                  const isEditing = editingId === m.id;
                  const isEditingDate = editingDateId === m.id;
                  return (
                    <tr key={m.id} style={{ borderBottom: '1px solid var(--color-border)', background: (isEditing || isEditingDate) ? 'var(--color-surface-hover)' : undefined }}>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                        {isEditingDate ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <input
                              autoFocus
                              type="datetime-local"
                              value={editDateValue}
                              onChange={(e) => setEditDateValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') commitDateEdit(m.id);
                                if (e.key === 'Escape') cancelDateEdit();
                              }}
                              style={{
                                width: '170px', padding: '3px 6px', fontFamily: 'monospace',
                                fontSize: '12px', border: '1px solid var(--ctp-blue)', borderRadius: '4px',
                                background: 'var(--color-surface)', color: 'var(--color-text)',
                              }}
                            />
                            <button
                              onClick={() => commitDateEdit(m.id)}
                              disabled={updateDateMutation.isPending}
                              title="Сохранить"
                              style={{ padding: '2px 6px', borderRadius: '4px', border: '1px solid var(--ctp-green)', background: 'var(--ctp-green)22', color: 'var(--ctp-green)', cursor: 'pointer', fontSize: '11px', fontWeight: 600 }}
                            >
                              ✓
                            </button>
                            <button
                              onClick={cancelDateEdit}
                              title="Отмена"
                              style={{ padding: '2px 6px', borderRadius: '4px', border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text-muted)', cursor: 'pointer', fontSize: '11px' }}
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <span
                            onClick={() => startDateEdit(m)}
                            title="Нажмите для редактирования даты"
                            style={{ color: 'var(--color-text-muted)', fontFamily: 'monospace', cursor: 'pointer', borderBottom: '1px dashed var(--color-border)' }}
                          >
                            {(() => {
                              const d = new Date(m.createdAt);
                              const date = d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
                              const time = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                              return `${date} ${time}`;
                            })()}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Wallet size={13} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
                          <span style={{ color: 'var(--color-text)' }}>{m.walletName}</span>
                          <span style={{ color: 'var(--color-text-muted)', fontSize: '11px' }}>({m.walletType})</span>
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                        {isEditing ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
                            <span style={{ color: 'var(--color-text-muted)', fontFamily: 'monospace', fontSize: '12px' }}>
                              {isPositive ? '+' : '-'}
                            </span>
                            <input
                              autoFocus
                              type="number"
                              min="0"
                              step="any"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') commitEdit(m.id);
                                if (e.key === 'Escape') cancelEdit();
                              }}
                              style={{
                                width: '90px', padding: '3px 6px', fontFamily: 'monospace',
                                fontSize: '13px', fontWeight: 600,
                                border: '1px solid var(--ctp-blue)', borderRadius: '4px',
                                background: 'var(--color-surface)', color: 'var(--color-text)',
                                textAlign: 'right',
                              }}
                            />
                            <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>{m.currency}</span>
                          </div>
                        ) : (
                          <span style={{ fontFamily: 'monospace', fontWeight: 600, color: isPositive ? 'var(--ctp-green)' : 'var(--ctp-red)' }}>
                            {formatAmt(m.amount)} {m.currency}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        {m.isDebtPayment ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '99px', fontSize: '11px', fontWeight: 600, background: 'var(--ctp-peach)22', color: 'var(--ctp-peach)', border: '1px solid var(--ctp-peach)44' }}>
                            {isPositive ? <ArrowDownLeft size={11} /> : <ArrowUpRight size={11} />}
                            Debt Payment
                          </span>
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '99px', fontSize: '11px', fontWeight: 600, background: 'var(--ctp-blue)22', color: 'var(--ctp-blue)', border: '1px solid var(--ctp-blue)44' }}>
                            {isPositive ? <ArrowDownLeft size={11} /> : <ArrowUpRight size={11} />}
                            Regular
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                        {isEditing ? (
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                            <button
                              onClick={() => commitEdit(m.id)}
                              disabled={updateMutation.isPending}
                              title="Сохранить"
                              style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--ctp-green)', background: 'var(--ctp-green)22', color: 'var(--ctp-green)', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                            >
                              ✓
                            </button>
                            <button
                              onClick={cancelEdit}
                              title="Отмена"
                              style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text-muted)', cursor: 'pointer', fontSize: '12px' }}
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startEdit(m)}
                            title="Редактировать сумму"
                            style={{ padding: '4px 6px', borderRadius: '4px', border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text-muted)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                          >
                            <Edit3 size={13} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      {/* Recalculate confirmation dialog */}
      {pendingUpdate && (
        <Modal
          isOpen
          onClose={() => { setPendingUpdate(null); setEditingId(null); }}
          title="Пересчитать транзакцию?"
          size="sm"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <p style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: '14px' }}>
              Обновить также сумму в связанной транзакции (<code>amount_in</code> / <code>amount_out</code>), чтобы она сходилась с движением?
            </p>
            <p style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: '13px', opacity: 0.8 }}>
              Если нет — транзакция будет <strong>переоткрыта</strong>, а долг установлен как разница между суммой транзакции и фактическим движением.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
              <button
                onClick={() => updateMutation.mutate({ ...pendingUpdate, recalculateTransaction: true })}
                disabled={updateMutation.isPending}
                style={{ padding: '10px 16px', borderRadius: '6px', border: 'none', background: 'var(--ctp-blue)', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}
              >
                Да, пересчитать транзакцию
              </button>
              <button
                onClick={() => updateMutation.mutate({ ...pendingUpdate, recalculateTransaction: false })}
                disabled={updateMutation.isPending}
                style={{ padding: '10px 16px', borderRadius: '6px', border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', cursor: 'pointer', fontSize: '14px' }}
              >
                Нет, только движение
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

function EditModal({
  transaction,
  entityId,
  onClose,
  onSave,
  isLoading,
}: {
  transaction: any;
  entityId: string;
  onClose: () => void;
  onSave: (data: any) => void;
  isLoading: boolean;
}) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // ─── Core fields ───────────────────────────────────
  const [type, setType] = useState(transaction.type || 'exchange');
  const [status, setStatus] = useState(transaction.status || 'closed');
  const [comment, setComment] = useState(transaction.comment || '');

  // ─── Dates ─────────────────────────────────────────
  const [transactionDate, setTransactionDate] = useState(
    transaction.transactionDate ? transaction.transactionDate.slice(0, 10) : ''
  );
  const [createdAt, setCreatedAt] = useState(() => {
    if (!transaction.createdAt) return '';
    const d = new Date(transaction.createdAt);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [expectedPaymentDate, setExpectedPaymentDate] = useState(
    transaction.expectedPaymentDate ? transaction.expectedPaymentDate.slice(0, 10) : ''
  );
  const [expectedPaymentDateIn, setExpectedPaymentDateIn] = useState(
    transaction.expectedPaymentDateIn ? transaction.expectedPaymentDateIn.slice(0, 10) : ''
  );
  const [expectedPaymentDateOut, setExpectedPaymentDateOut] = useState(
    transaction.expectedPaymentDateOut ? transaction.expectedPaymentDateOut.slice(0, 10) : ''
  );

  // ─── Exchange amounts ──────────────────────────────
  const [amountIn, setAmountIn] = useState(transaction.amountIn || '');
  const [currencyIn, setCurrencyIn] = useState(transaction.currencyIn || '');
  const [walletInId, setWalletInId] = useState(transaction.walletInId || '');
  const [amountOut, setAmountOut] = useState(transaction.amountOut || '');
  const [currencyOut, setCurrencyOut] = useState(transaction.currencyOut || '');
  const [walletOutId, setWalletOutId] = useState(transaction.walletOutId || '');
  const [exchangeRate] = useState(transaction.exchangeRate || '');
  const [oracleRate, setOracleRate] = useState(transaction.oracleRate || '');

  // ─── Single-currency fields ────────────────────────
  const [amount, setAmount] = useState(transaction.amount || '');
  const [currency, setCurrency] = useState(transaction.currency || '');
  const [walletId, setWalletId] = useState(transaction.walletId || '');
  const [category, setCategory] = useState(transaction.category || '');

  // ─── Debt tracking ─────────────────────────────────
  const [expectedAmount, setExpectedAmount] = useState(transaction.expectedAmount || '');
  const [debtAmount, setDebtAmount] = useState(transaction.debtAmount || '');
  const [debtAmountIn, setDebtAmountIn] = useState(transaction.debtAmountIn || '');
  const [debtAmountOut, setDebtAmountOut] = useState(transaction.debtAmountOut || '');

  // ─── Financial ─────────────────────────────────────
  const [profitAmount, setProfitAmount] = useState(transaction.profitAmount || '');
  const [profitCurrency, setProfitCurrency] = useState(transaction.profitCurrency || '');

  // ─── Relations ─────────────────────────────────────
  const [legalEntityId, setLegalEntityId] = useState(transaction.legalEntityId || '');
  const [employeeId, setEmployeeId] = useState(transaction.employeeId || '');
  const [clientUuid, setClientUuid] = useState(transaction.clientId || ''); // clientId in frontend = client_uuid in DB
  const [clientIdLegacy, setClientIdLegacy] = useState(''); // legacy varchar field – not returned by API, so empty

  // ─── TX hashes ─────────────────────────────────────
  const [txHashIn, setTxHashIn] = useState(transaction.txHashIn || '');
  const [txHashOut, setTxHashOut] = useState(transaction.txHashOut || '');
  const [txHashSingle, setTxHashSingle] = useState(transaction.txHash || '');

  // ─── Data fetching ─────────────────────────────────
  const { data: walletsData, isLoading: walletsLoading } = useQuery({
    queryKey: ['wallets', entityId],
    queryFn: () => api.admin.wallets.list(entityId),
  });

  // Admin-only data fetches
  const { data: entitiesData } = useQuery({
    queryKey: ['admin-entities'],
    queryFn: () => api.admin.entities.list(),
    enabled: isAdmin,
  });

  const { data: usersData } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.admin.users.list(),
    enabled: isAdmin,
  });

  const { data: clientsData } = useQuery({
    queryKey: ['clients', entityId, 'all'],
    queryFn: () => api.clients.list(entityId, { limit: 1000 }),
    enabled: isAdmin,
  });

  const { data: categoriesData } = useQuery({
    queryKey: ['admin-categories'],
    queryFn: () => api.admin.categories.list(),
    enabled: isAdmin,
  });

  const allWallets = walletsData?.data ?? [];
  const entities = entitiesData?.data ?? [];
  const users = usersData?.data ?? [];
  const clients = clientsData?.data ?? [];
  const categories = categoriesData?.data ?? [];

  // ─── Computed values ───────────────────────────────
  const createdAtChanged = (() => {
    if (!createdAt || !transaction.createdAt) return false;
    const orig = new Date(transaction.createdAt);
    const pad = (n: number) => n.toString().padStart(2, '0');
    const origStr = `${orig.getFullYear()}-${pad(orig.getMonth() + 1)}-${pad(orig.getDate())}T${pad(orig.getHours())}:${pad(orig.getMinutes())}`;
    return createdAt !== origStr;
  })();

  // For non-admin wallet filtering by currency
  const walletsIn = allWallets.filter(w => w.currencyCode === transaction.currencyIn);
  const walletsOut = allWallets.filter(w => w.currencyCode === transaction.currencyOut);
  const walletsSingle = allWallets.filter(w => w.currencyCode === transaction.currency);

  const isExchange = transaction.type === 'exchange';
  const isSingle = ['expense', 'deposit', 'withdrawal'].includes(transaction.type);

  // ─── Submit handler ────────────────────────────────
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isAdmin) {
      // Admin deep edit: send every field that changed
      const data: Record<string, any> = {};

      // Helper to add only changed values
      const addIfChanged = (key: string, newVal: any, origVal: any) => {
        const nv = newVal === '' ? undefined : newVal;
        const ov = origVal === '' || origVal == null ? undefined : origVal;
        if (nv !== ov) data[key] = nv ?? null;
      };

      // Core
      addIfChanged('type', type, transaction.type);
      addIfChanged('status', status, transaction.status);
      addIfChanged('comment', comment, transaction.comment);

      // Exchange amounts
      addIfChanged('amountIn', amountIn, transaction.amountIn);
      addIfChanged('currencyIn', currencyIn, transaction.currencyIn);
      addIfChanged('walletInId', walletInId, transaction.walletInId);
      addIfChanged('amountOut', amountOut, transaction.amountOut);
      addIfChanged('currencyOut', currencyOut, transaction.currencyOut);
      addIfChanged('walletOutId', walletOutId, transaction.walletOutId);
      const computedRate = calculateDisplayRate({ currencyIn, currencyOut, amountIn, amountOut });
      addIfChanged('exchangeRate', computedRate ? String(computedRate) : exchangeRate, transaction.exchangeRate);
      addIfChanged('oracleRate', oracleRate, transaction.oracleRate);

      // Single amounts
      addIfChanged('amount', amount, transaction.amount);
      addIfChanged('currency', currency, transaction.currency);
      addIfChanged('walletId', walletId, transaction.walletId);
      addIfChanged('category', category, transaction.category);

      // Debt
      addIfChanged('expectedAmount', expectedAmount, transaction.expectedAmount);
      addIfChanged('debtAmount', debtAmount, transaction.debtAmount);
      addIfChanged('debtAmountIn', debtAmountIn, transaction.debtAmountIn);
      addIfChanged('debtAmountOut', debtAmountOut, transaction.debtAmountOut);

      // Dates
      addIfChanged('transactionDate', transactionDate, transaction.transactionDate?.slice(0, 10));
      addIfChanged('expectedPaymentDate', expectedPaymentDate, transaction.expectedPaymentDate?.slice(0, 10));
      addIfChanged('expectedPaymentDateIn', expectedPaymentDateIn, transaction.expectedPaymentDateIn?.slice(0, 10));
      addIfChanged('expectedPaymentDateOut', expectedPaymentDateOut, transaction.expectedPaymentDateOut?.slice(0, 10));

      // Financial
      addIfChanged('profitAmount', profitAmount, transaction.profitAmount);
      addIfChanged('profitCurrency', profitCurrency, transaction.profitCurrency);

      // TX hashes
      addIfChanged('txHashIn', txHashIn, transaction.txHashIn);
      addIfChanged('txHashOut', txHashOut, transaction.txHashOut);
      addIfChanged('txHash', txHashSingle, transaction.txHash);

      // Relations
      addIfChanged('legalEntityId', legalEntityId, transaction.legalEntityId);
      addIfChanged('employeeId', employeeId, transaction.employeeId);
      if (clientUuid !== (transaction.clientId || '')) {
        data.clientUuid = clientUuid || null;
      }
      if (clientIdLegacy) {
        data.clientId = clientIdLegacy;
      }

      // createdAt
      if (createdAtChanged && createdAt) {
        const utcDate = new Date(`${createdAt}:00`);
        data.createdAt = utcDate.toISOString();
      }

      onSave(data);
    } else {
      // Non-admin: limited fields
      const data: Record<string, string | undefined> = {
        comment: comment || undefined,
        transactionDate: transactionDate || undefined,
      };
      if (createdAtChanged && createdAt) {
        const utcDate = new Date(`${createdAt}:00`);
        data.createdAt = utcDate.toISOString();
      }
      if (isExchange) {
        if (amountIn !== (transaction.amountIn || '')) data.amountIn = amountIn || undefined;
        if (amountOut !== (transaction.amountOut || '')) data.amountOut = amountOut || undefined;
        if (debtAmountIn !== (transaction.debtAmountIn || '')) data.debtAmountIn = debtAmountIn || undefined;
        if (debtAmountOut !== (transaction.debtAmountOut || '')) data.debtAmountOut = debtAmountOut || undefined;
        if (walletInId && walletInId !== transaction.walletInId) data.walletInId = walletInId;
        if (walletOutId && walletOutId !== transaction.walletOutId) data.walletOutId = walletOutId;
        if (txHashIn !== (transaction.txHashIn || '')) data.txHashIn = txHashIn || null;
        if (txHashOut !== (transaction.txHashOut || '')) data.txHashOut = txHashOut || null;
      } else if (isSingle) {
        if (amount !== (transaction.amount || '')) data.amount = amount || undefined;
        if (walletId && walletId !== transaction.walletId) data.walletId = walletId;
        if (txHashSingle !== (transaction.txHash || '')) data.txHash = txHashSingle || null;
      }
      onSave(data);
    }
  };

  // ─── Styles ────────────────────────────────────────
  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 10px',
    border: '1px solid var(--color-border)', borderRadius: '6px',
    fontSize: '13px', color: 'var(--color-text)', background: 'var(--color-surface)',
    outline: 'none', transition: 'border-color 0.15s', boxSizing: 'border-box',
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle, cursor: 'pointer',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '10px', fontWeight: 600, color: 'var(--color-text-muted)',
    textTransform: 'uppercase', letterSpacing: '0.5px',
  };

  const sectionStyle: React.CSSProperties = {
    display: 'flex', flexDirection: 'column', gap: '12px',
    padding: '12px 0',
    borderBottom: '1px solid var(--color-border)',
  };

  const sectionLabelStyle: React.CSSProperties = {
    fontSize: '11px', fontWeight: 700, color: 'var(--ctp-teal)',
    textTransform: 'uppercase', letterSpacing: '0.8px',
    marginBottom: '4px',
  };

  const gridStyle: React.CSSProperties = {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px',
  };

  const fieldBlock = (label: string, content: React.ReactNode, span2?: boolean) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', ...(span2 ? { gridColumn: 'span 2' } : {}) }}>
      <label style={labelStyle}>{label}</label>
      {content}
    </div>
  );

  // Helper for select dropdown rendering
  const renderSelect = (value: string, onChange: (v: string) => void, options: { value: string; label: string }[], placeholder?: string) => (
    <select value={value} onChange={e => onChange(e.target.value)} style={selectStyle}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );

  const renderInput = (value: string, onChange: (v: string) => void, type = 'text', extra?: React.CSSProperties) => (
    <input type={type} value={value} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, ...extra }} />
  );

  // ─── Render ────────────────────────────────────────
  if (isAdmin) {
    return (
      <Modal isOpen={true} onClose={onClose} title="Глубокое редактирование (Admin)" size="lg">
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0', maxHeight: '70vh', overflow: 'auto', padding: '0 4px' }}>

            {/* ─── Warning banner ─── */}
            <div style={{
              padding: '8px 12px', borderRadius: '6px', fontSize: '12px', lineHeight: '1.5',
              background: 'var(--ctp-peach)15', border: '1px solid var(--ctp-peach)33',
              color: 'var(--ctp-peach)', margin: '8px 0',
            }}>
              ⚠️ Режим глубокого редактирования. Изменение полей может привести к несоответствию данных. Используйте осознанно.
            </div>

            {/* ─── Section: Core ─── */}
            <div style={sectionStyle}>
              <div style={sectionLabelStyle}>Основные</div>
              <div style={gridStyle}>
                {fieldBlock('Тип транзакции (type)', renderSelect(type, setType, [
                  { value: 'exchange', label: 'Exchange' },
                  { value: 'expense', label: 'Expense' },
                  { value: 'deposit', label: 'Deposit' },
                  { value: 'withdrawal', label: 'Withdrawal' },
                  { value: 'transfer', label: 'Transfer' },
                ]))}
                {fieldBlock('Статус (status)', renderSelect(status, setStatus, [
                  { value: 'open', label: 'Open' },
                  { value: 'closed', label: 'Closed' },
                ]))}
              </div>
            </div>

            {/* ─── Section: Relations ─── */}
            <div style={sectionStyle}>
              <div style={sectionLabelStyle}>Связи</div>
              <div style={gridStyle}>
                {fieldBlock('Юр. лицо (legal_entity_id)', renderSelect(
                  legalEntityId, setLegalEntityId,
                  entities.map(e => ({ value: e.id, label: `${e.code} — ${e.name}` })),
                  '— выбрать —'
                ))}
                {fieldBlock('Сотрудник (employee_id)', renderSelect(
                  employeeId, setEmployeeId,
                  users.map(u => ({ value: u.id, label: `${u.fullName} (${u.role})` })),
                  '— выбрать —'
                ))}
                {fieldBlock('Клиент (client_uuid)', renderSelect(
                  clientUuid, v => setClientUuid(v),
                  clients.map(c => ({ value: c.id, label: c.name })),
                  '— нет —'
                ))}
                {fieldBlock('Client ID (legacy varchar)', renderInput(clientIdLegacy, setClientIdLegacy))}
              </div>
            </div>

            {/* ─── Section: Exchange Amounts ─── */}
            <div style={sectionStyle}>
              <div style={sectionLabelStyle}>Суммы входящие / исходящие (Exchange)</div>
              <div style={gridStyle}>
                {fieldBlock('amount_in', renderInput(amountIn, setAmountIn, 'number'))}
                {fieldBlock('currency_in', renderInput(currencyIn, setCurrencyIn))}
                {fieldBlock('wallet_in_id',
                  walletsLoading
                    ? <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', padding: '8px 0' }}>Загрузка...</span>
                    : renderSelect(walletInId, setWalletInId, allWallets.map(w => ({ value: w.id, label: `${w.name} (${w.currencyCode} / ${w.type})` })), '— нет —')
                )}
                {fieldBlock('exchange_rate', <span style={{ fontFamily: 'monospace', padding: '8px 0', display: 'block', color: 'var(--color-text-muted)' }}>{formatDisplayRate(calculateDisplayRate({ currencyIn, currencyOut, amountIn, amountOut }))}</span>)}
                {fieldBlock('amount_out', renderInput(amountOut, setAmountOut, 'number'))}
                {fieldBlock('currency_out', renderInput(currencyOut, setCurrencyOut))}
                {fieldBlock('wallet_out_id',
                  walletsLoading
                    ? <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', padding: '8px 0' }}>Загрузка...</span>
                    : renderSelect(walletOutId, setWalletOutId, allWallets.map(w => ({ value: w.id, label: `${w.name} (${w.currencyCode} / ${w.type})` })), '— нет —')
                )}
                {fieldBlock('oracle_rate', renderInput(oracleRate, setOracleRate, 'number'))}
              </div>
            </div>

            {/* ─── Section: Single-currency Amounts ─── */}
            <div style={sectionStyle}>
              <div style={sectionLabelStyle}>Суммы (single-currency: expense/deposit/withdrawal)</div>
              <div style={gridStyle}>
                {fieldBlock('amount', renderInput(amount, setAmount, 'number'))}
                {fieldBlock('currency', renderInput(currency, setCurrency))}
                {fieldBlock('wallet_id',
                  walletsLoading
                    ? <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', padding: '8px 0' }}>Загрузка...</span>
                    : renderSelect(walletId, setWalletId, allWallets.map(w => ({ value: w.id, label: `${w.name} (${w.currencyCode} / ${w.type})` })), '— нет —')
                )}
                {fieldBlock('category', renderSelect(
                  category, setCategory,
                  categories.map(c => ({ value: c.name, label: c.name })),
                  '— нет —'
                ))}
              </div>
            </div>

            {/* ─── Section: Dates ─── */}
            <div style={sectionStyle}>
              <div style={sectionLabelStyle}>Даты</div>
              <div style={gridStyle}>
                {fieldBlock('transaction_date (DATE OUT)', renderInput(transactionDate, setTransactionDate, 'date'))}
                {fieldBlock('created_at (DATE IN)',
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <input type="datetime-local" value={createdAt} onChange={e => setCreatedAt(e.target.value)}
                      style={{ ...inputStyle, borderColor: createdAtChanged ? 'var(--ctp-peach)' : undefined }}
                    />
                    {createdAtChanged && (
                      <div style={{
                        padding: '6px 10px', borderRadius: '4px', fontSize: '11px', lineHeight: '1.4',
                        background: 'var(--ctp-blue)15', border: '1px solid var(--ctp-blue)33',
                        color: 'var(--ctp-blue)',
                      }}>
                        ℹ️ Wallet movements не будут затронуты — управляйте ими вручную.
                      </div>
                    )}
                  </div>
                )}
                {fieldBlock('expected_payment_date (deprecated)', renderInput(expectedPaymentDate, setExpectedPaymentDate, 'date'))}
                {fieldBlock('expected_payment_date_in', renderInput(expectedPaymentDateIn, setExpectedPaymentDateIn, 'date'))}
                {fieldBlock('expected_payment_date_out', renderInput(expectedPaymentDateOut, setExpectedPaymentDateOut, 'date'))}
              </div>
            </div>

            {/* ─── Section: Debt ─── */}
            <div style={sectionStyle}>
              <div style={sectionLabelStyle}>Долги</div>
              <div style={gridStyle}>
                {fieldBlock('debt_amount_in', renderInput(debtAmountIn, setDebtAmountIn, 'number'))}
                {fieldBlock('debt_amount_out', renderInput(debtAmountOut, setDebtAmountOut, 'number'))}
                {fieldBlock('expected_amount (deprecated)', renderInput(expectedAmount, setExpectedAmount, 'number'))}
                {fieldBlock('debt_amount (deprecated)', renderInput(debtAmount, setDebtAmount, 'number'))}
              </div>
            </div>

            {/* ─── Section: Financial ─── */}
            <div style={sectionStyle}>
              <div style={sectionLabelStyle}>Финансовые (profit)</div>
              <div style={gridStyle}>
                {fieldBlock('profit_amount', renderInput(profitAmount, setProfitAmount, 'number'))}
                {fieldBlock('profit_currency', renderInput(profitCurrency, setProfitCurrency))}
              </div>
            </div>

            {/* ─── Section: Blockchain TX Hashes ─── */}
            {(() => {
              const wIn = allWallets.find(w => w.id === walletInId);
              const wOut = allWallets.find(w => w.id === walletOutId);
              const wSingle = allWallets.find(w => w.id === walletId);
              const anyCrypto =
                wIn?.type === 'crypto' || wOut?.type === 'crypto' || wSingle?.type === 'crypto';
              if (!anyCrypto) return null;
              return (
                <div style={sectionStyle}>
                  <div style={sectionLabelStyle}>Блокчейн (TX hash)</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {wIn?.type === 'crypto' && (
                      <TxHashField
                        label={`TX Hash IN (${wIn.currencyCode})`}
                        walletAddress={wIn.address}
                        walletType={wIn.type}
                        value={txHashIn}
                        onChange={setTxHashIn}
                        compact
                      />
                    )}
                    {wOut?.type === 'crypto' && (
                      <TxHashField
                        label={`TX Hash OUT (${wOut.currencyCode})`}
                        walletAddress={wOut.address}
                        walletType={wOut.type}
                        value={txHashOut}
                        onChange={setTxHashOut}
                        compact
                      />
                    )}
                    {wSingle?.type === 'crypto' && (
                      <TxHashField
                        label={`TX Hash (${wSingle.currencyCode})`}
                        walletAddress={wSingle.address}
                        walletType={wSingle.type}
                        value={txHashSingle}
                        onChange={setTxHashSingle}
                        compact
                      />
                    )}
                  </div>
                </div>
              );
            })()}

            {/* ─── Section: Comment ─── */}
            <div style={{ ...sectionStyle, borderBottom: 'none' }}>
              <div style={sectionLabelStyle}>Комментарий</div>
              {fieldBlock('comment',
                <textarea
                  rows={3}
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  placeholder="Добавить заметку..."
                  style={{ ...inputStyle, resize: 'vertical', minHeight: '60px', fontFamily: 'inherit' }}
                />,
                true
              )}
            </div>
          </div>

          {/* ─── Form Actions ─── */}
          <div style={{
            display: 'flex', gap: '12px', justifyContent: 'flex-end',
            paddingTop: '16px', borderTop: '1px solid var(--color-border)',
            marginTop: '8px',
          }}>
            <button type="button" onClick={onClose} style={{
              padding: '10px 20px', borderRadius: '6px', fontSize: '14px', fontWeight: 600,
              cursor: 'pointer', border: '1px solid var(--color-border)',
              background: 'var(--color-surface)', color: 'var(--color-text)',
            }}>
              Отмена
            </button>
            <button type="submit" disabled={isLoading} style={{
              padding: '10px 20px', borderRadius: '6px', fontSize: '14px', fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer', border: 'none',
              background: 'var(--ctp-teal)', color: '#ffffff', opacity: isLoading ? 0.6 : 1,
            }}>
              {isLoading ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </Modal>
    );
  }

  // ─── Non-admin (original simple form) ──────────────
  return (
    <Modal isOpen={true} onClose={onClose} title="Редактировать транзакцию" size="sm">
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '4px 0 20px' }}>

          {/* transaction_date */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={labelStyle}>
              Дата закрытия сделки (transaction date)
            </label>
            <input
              type="date"
              value={transactionDate}
              onChange={e => setTransactionDate(e.target.value)}
              style={inputStyle}
            />
          </div>

          {/* created_at (DATE_IN) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={labelStyle}>
              Дата поступления средств (date in)
            </label>
            <input
              type="datetime-local"
              value={createdAt}
              onChange={e => setCreatedAt(e.target.value)}
              style={{
                ...inputStyle,
                borderColor: createdAtChanged ? 'var(--ctp-peach)' : undefined,
              }}
            />
            {createdAtChanged && (
              <div style={{
                padding: '8px 12px', borderRadius: '6px', fontSize: '12px', lineHeight: '1.5',
                background: 'var(--ctp-blue)15', border: '1px solid var(--ctp-blue)33',
                color: 'var(--ctp-blue)',
              }}>
                ℹ️ Wallet movements не будут затронуты — управляйте ими вручную.
              </div>
            )}
          </div>

          {/* Amount IN — exchange only */}
          {isExchange && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={labelStyle}>
                Сумма входящая ({transaction.currencyIn})
              </label>
              <input
                type="number"
                step="any"
                value={amountIn}
                onChange={e => setAmountIn(e.target.value)}
                style={inputStyle}
              />
            </div>
          )}

          {/* Amount OUT — exchange only */}
          {isExchange && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={labelStyle}>
                Сумма исходящая ({transaction.currencyOut})
              </label>
              <input
                type="number"
                step="any"
                value={amountOut}
                onChange={e => setAmountOut(e.target.value)}
                style={inputStyle}
              />
            </div>
          )}

          {/* Amount — single-currency only */}
          {isSingle && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={labelStyle}>
                Сумма ({transaction.currency})
              </label>
              <input
                type="number"
                step="any"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                style={inputStyle}
              />
            </div>
          )}

          {/* Wallet IN — exchange only */}
          {isExchange && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={labelStyle}>
                Кошелёк входящий ({transaction.currencyIn})
              </label>
              {walletsLoading ? (
                <div style={{ fontSize: '13px', color: 'var(--color-text-muted)', padding: '10px 0' }}>Загрузка...</div>
              ) : (
                <select value={walletInId} onChange={e => setWalletInId(e.target.value)} style={selectStyle}>
                  <option value="">— не изменять —</option>
                  {walletsIn.map(w => (
                    <option key={w.id} value={w.id}>
                      {w.name} ({w.type})
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Wallet OUT — exchange only */}
          {isExchange && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={labelStyle}>
                Кошелёк исходящий ({transaction.currencyOut})
              </label>
              {walletsLoading ? (
                <div style={{ fontSize: '13px', color: 'var(--color-text-muted)', padding: '10px 0' }}>Загрузка...</div>
              ) : (
                <select value={walletOutId} onChange={e => setWalletOutId(e.target.value)} style={selectStyle}>
                  <option value="">— не изменять —</option>
                  {walletsOut.map(w => (
                    <option key={w.id} value={w.id}>
                      {w.name} ({w.type})
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Wallet single — expense/deposit/withdrawal */}
          {isSingle && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={labelStyle}>
                Кошелёк ({transaction.currency})
              </label>
              {walletsLoading ? (
                <div style={{ fontSize: '13px', color: 'var(--color-text-muted)', padding: '10px 0' }}>Загрузка...</div>
              ) : (
                <select value={walletId} onChange={e => setWalletId(e.target.value)} style={selectStyle}>
                  <option value="">— не изменять —</option>
                  {walletsSingle.map(w => (
                    <option key={w.id} value={w.id}>
                      {w.name} ({w.type})
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Debt Amount IN — exchange only */}
          {isExchange && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={labelStyle}>
                Долг входящий ({transaction.currencyIn})
              </label>
              <input
                type="number"
                step="any"
                value={debtAmountIn}
                onChange={e => setDebtAmountIn(e.target.value)}
                style={inputStyle}
              />
            </div>
          )}

          {/* Debt Amount OUT — exchange only */}
          {isExchange && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={labelStyle}>
                Долг исходящий ({transaction.currencyOut})
              </label>
              <input
                type="number"
                step="any"
                value={debtAmountOut}
                onChange={e => setDebtAmountOut(e.target.value)}
                style={inputStyle}
              />
            </div>
          )}

          {/* TX Hashes — only when at least one wallet is crypto */}
          {(() => {
            const effWalletInId = walletInId || transaction.walletInId;
            const effWalletOutId = walletOutId || transaction.walletOutId;
            const effWalletId = walletId || transaction.walletId;
            const wIn = allWallets.find(w => w.id === effWalletInId);
            const wOut = allWallets.find(w => w.id === effWalletOutId);
            const wSingle = allWallets.find(w => w.id === effWalletId);
            return (
              <>
                {isExchange && wIn?.type === 'crypto' && (
                  <TxHashField
                    label={`TX Hash IN (${transaction.currencyIn})`}
                    walletAddress={wIn.address}
                    walletType={wIn.type}
                    value={txHashIn}
                    onChange={setTxHashIn}
                    compact
                  />
                )}
                {isExchange && wOut?.type === 'crypto' && (
                  <TxHashField
                    label={`TX Hash OUT (${transaction.currencyOut})`}
                    walletAddress={wOut.address}
                    walletType={wOut.type}
                    value={txHashOut}
                    onChange={setTxHashOut}
                    compact
                  />
                )}
                {isSingle && wSingle?.type === 'crypto' && (
                  <TxHashField
                    label={`TX Hash (${transaction.currency})`}
                    walletAddress={wSingle.address}
                    walletType={wSingle.type}
                    value={txHashSingle}
                    onChange={setTxHashSingle}
                    compact
                  />
                )}
              </>
            );
          })()}

          {/* comment */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={labelStyle}>
              Комментарий
            </label>
            <textarea
              rows={4}
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Добавить заметку..."
              style={{
                ...inputStyle, resize: 'vertical', minHeight: '90px', fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        <div style={{
          display: 'flex', gap: '12px', justifyContent: 'flex-end',
          paddingTop: '16px', borderTop: '1px solid var(--color-border)',
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '10px 20px', borderRadius: '6px', fontSize: '14px', fontWeight: 600,
              cursor: 'pointer', border: '1px solid var(--color-border)',
              background: 'var(--color-surface)', color: 'var(--color-text)',
            }}
          >
            Отмена
          </button>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              padding: '10px 20px', borderRadius: '6px', fontSize: '14px', fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer', border: 'none',
              background: 'var(--ctp-teal)', color: '#ffffff', opacity: isLoading ? 0.6 : 1,
            }}
          >
            {isLoading ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
